// Running an analysis: submit a game, wait for the engine, get a report.

import { api, postJson, readJson, sleep } from "../app/api.ts";

export interface Job {
  status: string;
  stage?: string;
  done?: number;
  total?: number;
}

const STAGES: Record<string, string> = {
  scanning: "Reading the whole game",
  verifying: "Checking what it found",
  done: "Done",
};

export class NeedsColor extends Error {
  readonly names: { white: string; black: string };
  constructor(message: string, names: { white: string; black: string }) {
    super(message);
    this.names = names;
  }
}

/** Run one analysis to completion: submit, then poll until the job settles.
 *  Throws `NeedsColor` when the server has to ask whose side. */
export async function runAnalysis(body: unknown, onProgress?: (job: Job) => void): Promise<any> {
  const res = await postJson("/api/analyze", body);
  const data = await readJson(res);
  if (!res.ok) {
    if (data.needs_color) throw new NeedsColor(data.error || "Which side?", data.needs_color);
    throw new Error(data.error || "The request failed.");
  }
  for (;;) {
    const job = await readJson(await api("/api/jobs/" + data.job_id));
    if (job.status === "done") return job.report;
    if (job.status === "failed") throw new Error(job.error || "The analysis failed.");
    onProgress?.(job);
    await sleep(900);
  }
}

export function ProgressCard({ job }: { job: Job }) {
  const pct = job.total ? Math.round(((job.done || 0) / job.total) * 100) : 0;
  const label = job.status === "queued" ? "Waiting for the engine" : STAGES[job.stage || ""] || "Working";
  return (
    <div className="card pad analyse-status">
      <div className="stage-row">
        <b>{label}…</b>
        <span className="muted num">{job.total ? `${job.done} / ${job.total}` : ""}</span>
      </div>
      <div className={"progress" + (job.total ? "" : " indeterminate")}>
        <div style={{ width: pct + "%" }} />
      </div>
      <p className="help">About a minute for a full game. A game already analysed opens at once.</p>
    </div>
  );
}
